import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import type { ToastProps } from '../Toast/types';

interface ToastContextType {
  toastProps: ToastProps | null;
  shouldShowToast: boolean;
  showToast: (props: ToastProps) => void;
  hideToast: () => void;
}

const ToastContext = createContext<ToastContextType | undefined>(undefined);

export const ToastProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [toastProps, setToastProps] = useState<ToastProps | null>(null);
  const [shouldShowToast, setShouldShowToast] = useState(false);
  const toastTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearToastTimeout = useCallback(() => {
    if (toastTimeoutRef.current) {
      clearTimeout(toastTimeoutRef.current);
      toastTimeoutRef.current = null;
    }
  }, []);

  const hideToast = useCallback(() => {
    clearToastTimeout();
    setShouldShowToast(false);
  }, [clearToastTimeout]);

  const showToast = useCallback(
    (props: ToastProps) => {
      if (props?.content && props?.type) {
        clearToastTimeout();
        setToastProps(props);
        setShouldShowToast(true);

        toastTimeoutRef.current = setTimeout(() => {
          setShouldShowToast(false);
        }, props?.duration ?? 4000);
      }
    },
    [clearToastTimeout],
  );

  useEffect(() => () => clearToastTimeout(), [clearToastTimeout]);

  return (
    <ToastContext.Provider
      value={{
        toastProps,
        shouldShowToast,
        showToast,
        hideToast,
      }}
    >
      {children}
    </ToastContext.Provider>
  );
};

export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) throw new Error('useToast must be used within a ToastProvider');
  return context;
};
